import { useState, useCallback, useEffect } from 'react';
import { Page, Layout, Card, Form, FormLayout, TextField, Toast } from '@shopify/polaris';
import { TheSaveBar } from '../../shared/components/theSaveBar';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import { isAccountingRole } from '../../utils/userRoles.js';
import { getRequest, putRequest } from '../../shared/components/functions.jsx';
import { flattenEntry } from '../../shared/components/settingsFunctions.js';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import { EmailSignatureGenerator } from './EmailSignatureGenerator';

function formatDateForInput(value) {
  if (!value) return '';
  if (typeof value === 'string' && value.includes('T')) return value.split('T')[0];
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toISOString().split('T')[0];
}

function normalizeUser(...sources) {
  const merged = Object.assign({}, ...sources.filter(Boolean));

  return {
    ...merged,
    id: merged.id || merged.GebruikerID,
    username: merged.username || merged.Gebruiker || '',
    full_name: merged.full_name || merged.Gebruikernaam || '',
    email: merged.email || merged.Email || '',
    birthday: formatDateForInput(merged.birthday || merged.Birthday),
    DirectPhoneLine: merged.DirectPhoneLine || '',
    JobTitle: merged.JobTitle || ''
  };
}

export default function AccountPage() {
  const { user, setUser } = useAuthStore();
  const businessunits = useLookupStore((state) => state.businessunits);
  const setBusinessunits = useLookupStore((state) => state.setBusinessunits);
  const [initialUser, setInitialUser] = useState(() => normalizeUser(user));
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSaveError, setIsSaveError] = useState(false);
  const toggleToast = useCallback(() => setIsSuccess((isSuccess) => !isSuccess), []);
  const toastMarkup = isSuccess ? (
    <Toast content="Changes saved" onDismiss={toggleToast} />
  ) : isSaveError ? (
    <Toast content="Your changes could not be saved" error onDismiss={() => setIsSaveError(false)} />
  ) : null;
  // Email is the sign-in key, so only admins may change it
  const canEditEmail = isAccountingRole(user?.role);

  const url = `/api/users/${initialUser.id}/account`;

  useEffect(() => {
    let isMounted = true;

    async function loadAccountUser() {
      if (!user?.id) return;

      const accountRows = await getRequest(`/api/users/${user.id}`);
      const accountRow = Array.isArray(accountRows) ? accountRows[0] : accountRows;
      if (!isMounted) return;

      setInitialUser(normalizeUser(user, accountRow));
    }

    loadAccountUser();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    const needsSignatureFields =
      !Array.isArray(businessunits) ||
      businessunits.length === 0 ||
      businessunits.some(
        (unit) =>
          !Object.prototype.hasOwnProperty.call(unit, 'EmailSignatureBannerUrl') ||
          !Object.prototype.hasOwnProperty.call(unit, 'EmailSignatureBrandColor') ||
          !Object.prototype.hasOwnProperty.call(unit, 'EmailSignatureWebsite') ||
          !Object.prototype.hasOwnProperty.call(unit, 'EmailSignatureLinks')
      );

    if (needsSignatureFields) {
      setBusinessunits();
    }
  }, [businessunits, setBusinessunits]);

  const handleChange = (value, id) => {
    setInitialUser((initialUser) => ({ ...initialUser, [id]: value }));
    setIsDirty(true);
  };

  async function handleSave() {
    setIsSaving(true);
    let payload = flattenEntry({
      Gebruikernaam: { value: initialUser.full_name },
      Email: { value: initialUser.email },
      Birthday: { value: initialUser.birthday },
      DirectPhoneLine: { value: initialUser.DirectPhoneLine },
      JobTitle: { value: initialUser.JobTitle }
    });
    const saved = await putRequest(url, payload);
    if (!saved) {
      setIsSaving(false);
      setIsSaveError(true);
      return;
    }
    const nextUser = normalizeUser(user, initialUser);
    localStorage.setItem('currentUser', JSON.stringify(nextUser));
    setUser(nextUser);
    setIsSaving(false);
    setIsDirty(false);
    toggleToast();
  }

  async function handleDiscard() {
    const prevUser = await JSON.parse(localStorage.getItem('currentUser'));
    setInitialUser(normalizeUser(user, prevUser));
    setIsDirty(false);
  }

  return (
    <Page title="Account settings">
      {toastMarkup}
      <TheSaveBar isDirty={isDirty} savingResult={isSaving} handleDiscard={handleDiscard} handleSave={handleSave} />
      <Layout>
        <Layout.Section>
          <Card>
            <CardHeading title="Edit account information" />
            <Form onSubmit={handleSave} autoComplete="off">
              <FormLayout>
                <FormLayout.Group>
                  <TextField id="username" label="Username" value={initialUser.username} onChange={handleChange} maxLength={10} showCharacterCount disabled autoComplete="off" />
                  <TextField id="full_name" label="Name" value={initialUser.full_name} onChange={handleChange} maxLength={50} showCharacterCount autoComplete="off" />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    id="email"
                    label="E-Mail"
                    value={initialUser.email}
                    onChange={handleChange}
                    maxLength={50}
                    showCharacterCount
                    disabled={!canEditEmail}
                    helpText={canEditEmail ? undefined : 'Used to sign in, ask an admin to change it'}
                    autoComplete="off"
                  />
                  <TextField id="DirectPhoneLine" label="Direct phone line" value={initialUser.DirectPhoneLine} onChange={handleChange} maxLength={50} showCharacterCount autoComplete="off" />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField id="JobTitle" label="Job title" value={initialUser.JobTitle} onChange={handleChange} maxLength={100} showCharacterCount autoComplete="off" />
                  <TextField type="date" id="birthday" label="Birthday" value={initialUser.birthday} onChange={handleChange} autoComplete="off" />
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Card>
        </Layout.Section>
        <Layout.Section>
          <Card>
            <EmailSignatureGenerator accountUser={initialUser} businessUnits={businessunits} />
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
